import React, { useState, useEffect } from 'react';
import IconMorph from '../ui/IconMorph';
import Tooltip from '../ui/Tooltip';
import { PcCase, Computer, LaptopMinimal, Cctv, ScanEye, Webcam, Palette, Brush, PaintBucket, Package, Box, Layers, HelpCircle } from 'lucide-react';
import { loadServices, type ServiceData } from '../../utils/content-loader';

const ServicesSection: React.FC = () => {
  const iconRefs = React.useRef<(import('../ui/IconMorph').IconMorphHandle | null)[]>([]);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const hoveredCardRef = React.useRef<number | null>(null);
  const lastInteractionType = React.useRef<'hover' | 'touch' | 'scroll'>('scroll');
  const [hoveredCard, setHoveredCard] = React.useState<number | null>(null);
  const [lastHoveredCard, setLastHoveredCard] = React.useState(0);
  const [showTouchGlow, setShowTouchGlow] = React.useState(false);
  
  // Initialize with default services
  const defaultServices: ServiceData[] = [
    {
      order: 1,
      slug: '01-website-development',
      title: 'Modern Website Development',
      description: 'Fast, secure websites tailored to your needs — mobile-first, search-optimized, and future-ready. Choose a simple Static Presence, a professional business site, or an NGO-focused Community Hub.',
      icon: 'PcCase',
      iconAlternates: ['Computer', 'LaptopMinimal'],
      includes: [
        'Mobile-First Responsive Layout',
        'Content Management Setup',
        'SEO, Secure Hosting & Maintenance',
        { text: 'PWA App for Mobile & PC', hasTooltip: true }
      ]
    },
    {
      order: 2,
      slug: '02-cctv-streaming',
      title: 'CCTV Stream-to-Web',
      description: 'Powered by SentelBox — our proprietary hardware gateway — we connect your cameras and stream them securely with zero port forwarding or Static IP tax. Get password-protected embed links for your website, simple and hassle-free.',
      icon: 'Cctv',
      iconAlternates: ['ScanEye', 'Webcam'],
      includes: [
        'Powered by SentelBox (Our Proprietary Gateway)',
        'Secure Outbound Tunneling (no Static IP needed)',
        'Password-Protected Live Streams',
        'Cross-Platform Embed Links & Technical Support'
      ]
    },
    {
      order: 3,
      slug: '03-design-branding',
      title: 'Design & Branding',
      description: 'Strong visual identity sets you apart. We craft logos, brand systems, and digital design assets that connect with your audience.',
      icon: 'Palette',
      iconAlternates: ['Brush', 'PaintBucket'],
      includes: [
        'Logo & Brand Identity Design',
        'Visual Guidelines & Templates',
        'Marketing Collateral (social, print, digital)',
        'UI/UX Design for Apps & Websites'
      ]
    },
    {
      order: 4,
      slug: '04-product-packaging',
      title: 'Product & Packaging Design',
      description: 'Elevate your physical products with market-ready packaging design. We craft custom dielines, print-ready graphics, and unboxing experiences built to boost shelf appeal and sales.',
      icon: 'Package',
      iconAlternates: ['Box', 'Layers'],
      includes: [
        'Custom Dieline & Structural Layouts',
        'Print-Ready Artwork & Vector Files',
        'Material & Finish Specifications',
        'Label & Unboxing Design Systems'
      ]
    }
  ];
  
  const [services, setServices] = useState<ServiceData[]>(defaultServices);
  const [isLoading] = useState(false); // Set to false since we have default data

  // Try to load services from markdown files
  useEffect(() => {
    loadServices().then((loadedServices) => {
      if (loadedServices && loadedServices.length > 0) {
        setServices(loadedServices);
      }
    }).catch(() => {
      // Keep using default services
    });
  }, []);

  // Map icon names to components
  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      'PcCase': <PcCase key="pc" />,
      'Computer': <Computer key="computer" />,
      'LaptopMinimal': <LaptopMinimal key="laptop" />,
      'Cctv': <Cctv key="cctv" />,
      'ScanEye': <ScanEye key="scaneye" />,
      'Webcam': <Webcam key="webcam" />,
      'Palette': <Palette key="palette" />,
      'Brush': <Brush key="brush" />,
      'PaintBucket': <PaintBucket key="paintbucket" />,
      'Package': <Package key="package" />,
      'Box': <Box key="box" />,
      'Layers': <Layers key="layers" />,
      'HelpCircle': <HelpCircle key="help" />
    };
    return iconMap[iconName] || <HelpCircle key="default" />;
  };

  // Create icon morph components for each service
  const getServiceIcon = (service: ServiceData, index: number) => {
    const icons = [service.icon, ...(service.iconAlternates || [])].map(getIconComponent);
    if (icons.length < 4) {
      icons.push(icons[0]); // Add the first icon again if we don't have 4
    }
    
    return (
      <IconMorph
        ref={(el) => (iconRefs.current[index] = el)}
        triggerMode="external"
        icons={icons}
        size={36}
        strokeWidth={1.4}
        stepDurationMs={600}
        transitionStyle="motion"
      />
    );
  };

  // Handle scroll to specific card
  const scrollToCard = (index: number, behavior: ScrollBehavior = 'smooth') => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cards = container.querySelectorAll('[data-service-card]');
    const targetCard = cards[index] as HTMLElement;
    
    if (targetCard) {
      const containerRect = container.getBoundingClientRect();
      const cardRect = targetCard.getBoundingClientRect();
      const scrollLeft = targetCard.offsetLeft - (containerRect.width - cardRect.width) / 2;
      
      container.scrollTo({
        left: scrollLeft,
        behavior: behavior
      });
    }
  };

  // Check if card is fully visible and scroll if needed
  const scrollCardIntoViewIfNeeded = (index: number) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cards = container.querySelectorAll('[data-service-card]');
    const targetCard = cards[index] as HTMLElement;
    
    if (targetCard) {
      const containerRect = container.getBoundingClientRect();
      const cardRect = targetCard.getBoundingClientRect();
      
      // Check if card is fully visible
      const isFullyVisible = 
        cardRect.left >= containerRect.left && 
        cardRect.right <= containerRect.right;
      
      // If not fully visible, scroll it into view
      if (!isFullyVisible) {
        const scrollLeft = targetCard.offsetLeft - (containerRect.width - cardRect.width) / 2;
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  // Update active indicator based on scroll position
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Track interaction types
    const handleTouchStart = () => {
      lastInteractionType.current = 'touch';
    };

    // Reset glow on outside interactions
    const handleGlobalTouchStart = (e: TouchEvent) => {
      // Check if touch is outside the cards container
      if (!container.contains(e.target as Node)) {
        setShowTouchGlow(false);
      }
    };

    const handleGlobalClick = (e: MouseEvent) => {
      // Check if click is outside the cards container
      if (!container.contains(e.target as Node)) {
        setShowTouchGlow(false);
      }
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      // Reset glow if mouse moves outside the service section
      const serviceSection = container.closest('#services');
      if (serviceSection && !serviceSection.contains(e.target as Node)) {
        setShowTouchGlow(false);
      }
    };

    const handleGlobalWheel = () => {
      // Reset glow on any scroll/wheel action
      setShowTouchGlow(false);
    };

    const handleScroll = () => {
      // If not actively hovering, treat as scroll interaction and reset touch glow
      if (hoveredCardRef.current === null) {
        lastInteractionType.current = 'scroll';
        setShowTouchGlow(false); // Reset glow when scrolling
      }
      
      const cards = container.querySelectorAll('[data-service-card]');
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - containerCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      
      // Update lastHoveredCard based on interaction type:
      // - Always update on scroll/touch interactions
      // - Only update on hover if not currently hovering something else
      if (lastInteractionType.current !== 'hover' || hoveredCardRef.current === null) {
        setLastHoveredCard(closestIndex);
      }
    };

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    
    // Global event listeners to reset glow on outside interactions
    document.addEventListener('touchstart', handleGlobalTouchStart, { passive: true });
    document.addEventListener('click', handleGlobalClick, { passive: true });
    document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
    document.addEventListener('wheel', handleGlobalWheel, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchstart', handleGlobalTouchStart);
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('wheel', handleGlobalWheel);
    };
  }, []);

  // Show loading state while services are being loaded
  if (isLoading) {
    return (
      <section id="services" className="relative py-20 md:py-24 bg-background">
        <div className="container relative mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-foreground/60">Loading services...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="relative py-20 md:py-24 section-backdrop-light">

      <div className="section-grain" aria-hidden="true" />
      {/* Heading container with standard padding */}
      <div className="container relative mx-auto px-3 sm:px-4 lg:px-6">
        <div className="mb-16 relative z-10">
          <div className="flex items-center lg:hidden mb-3">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-2">[ 01 ]</span>
            <div className="w-20 border-t-2 border-foreground" />
          </div>
          <div className="hidden lg:flex flex-row items-baseline">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-6">[ 01 ]</span>
            <div>
              <div className="w-20 border-t-2 border-foreground mb-4"></div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">Our Services</h2>
            </div>
          </div>
          <div className="lg:hidden">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">Our Services</h2>
          </div>
        </div>
      </div>

      {/* Cards container - extends to viewport edge */}
      <div className="relative z-10">
        <div className="relative w-full">
          <div 
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto hide-scrollbar py-8"
            style={{
              // Removed snap and momentum scrolling for performance
              scrollBehavior: 'auto',
            }}
          >
            
            {/* Add significant negative space on the left for cards */}
            <div className="flex-shrink-0" style={{ width: '4rem' }}></div>
            <div className="hidden sm:block flex-shrink-0" style={{ width: '8rem' }}></div>
            <div className="hidden md:block flex-shrink-0" style={{ width: '12rem' }}></div>
            <div className="hidden lg:block flex-shrink-0" style={{ width: '16rem' }}></div>
            
            {services.map((service, index) => (
              <div
                key={index}
                data-service-card
                className="flex-shrink-0 w-[90vw] sm:w-[450px] md:w-[480px] lg:w-[520px] snap-center animate-on-scroll"
                data-delay={`${index * 0.2}s`}
              >
                <div 
                  className="group glow-card card-backdrop border-2 border-foreground transition-transform duration-200 hover:scale-[1.02] p-8 lg:p-10 h-full relative overflow-visible"
                  onClick={() => {
                    iconRefs.current[index]?.trigger();
                    setLastHoveredCard(index);
                    lastInteractionType.current = 'touch';
                    // Set touch glow for mobile clicks or desktop direct clicks
                    const isMobileDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                    if (isMobileDevice) {
                      // Clear hover state on mobile to prevent interference
                      setHoveredCard(null);
                      hoveredCardRef.current = null;
                      setShowTouchGlow(true);
                    } else if (hoveredCardRef.current === null) {
                      setShowTouchGlow(true);
                    }
                  }}
                  onMouseEnter={() => {
                    iconRefs.current[index]?.trigger();
                    setHoveredCard(index);
                    hoveredCardRef.current = index;
                    setLastHoveredCard(index);
                    lastInteractionType.current = 'hover';
                    setShowTouchGlow(false); // Reset touch glow when hovering
                    scrollCardIntoViewIfNeeded(index);
                  }}
                  onMouseLeave={() => {
                    setHoveredCard(null);
                    hoveredCardRef.current = null;
                  }}
                >
                  <div className="flex justify-between items-start mb-6 w-full">
                    <div className="text-left">{getServiceIcon(service, index)}</div>
                    
                    {/* Conditional Badge */}
                    {service.title === 'CCTV Stream-to-Web' && (
                      <div className="border border-[#1a1a1a] text-[#1a1a1a] font-mono text-[10px] sm:text-xs font-semibold tracking-widest uppercase px-5 py-1.5 rounded-full transition-all duration-300 group-hover:bg-[#1a1a1a] group-hover:text-white pointer-events-none">
                        SOLUTIONS
                      </div>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold mb-5 pr-12">{service.title}</h3>
                  <p className="text-base text-foreground/70 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="pt-8 border-t border-foreground/20">
                    <p className="font-mono text-xs uppercase tracking-widest mb-5 text-foreground/60">INCLUDES:</p>
                    <ul className="space-y-3">
                      {service.includes.map((item, idx) => {
                        const isTooltipItem = typeof item === 'object' && item.hasTooltip;
                        const itemText = typeof item === 'string' ? item : item.text;
                        const tooltipContent = typeof item === 'object' && item.tooltipContent ? item.tooltipContent : "PWA (Progressive Web App): A website that works like an app — installable on phone or computer, fast, and usable even offline.";
                        
                        return (
                          <li key={idx} className="flex items-start text-sm">
                            <span className="text-primary mr-3 mt-1">+</span>
                            <span className="text-foreground/80 flex items-center gap-2">
                              {itemText}
                              {isTooltipItem && (
                                <Tooltip content={tooltipContent}>
                                  <HelpCircle 
                                    size={14} 
                                    className="text-foreground/70 hover:text-primary transition-colors cursor-help flex-shrink-0 mt-0.5"  
                                  />
                                </Tooltip>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Add some padding at the end for better scrolling experience */}
            <div className="flex-shrink-0" style={{ width: '2rem' }}></div>
          </div>
        </div>
        
        {/* Scroll indicators */}
        <div className="flex justify-center gap-2 mt-6 relative z-10">
          {services.map((_, index) => {
            const isCurrentlyHovered = hoveredCard === index;
            const isLastHovered = lastHoveredCard === index;
            const shouldShowActive = isCurrentlyHovered || (hoveredCard === null && isLastHovered);
            const shouldShowGlow = isCurrentlyHovered || (showTouchGlow && isLastHovered);
            
            return (
              <div key={index} className="relative">
                {/* Glow effect behind the button */}
                {shouldShowGlow && (
                  <div 
                    className="absolute inset-0 rounded-full bg-primary blur-md animate-pulse"
                    style={{ 
                      transform: 'scale(1.5)',
                      opacity: 0.6
                    }}
                  />
                )}
                <button
                  onClick={() => {
                    scrollToCard(index);
                    setLastHoveredCard(index);
                    lastInteractionType.current = 'touch';
                    // Set touch glow for mobile clicks or desktop direct clicks
                    const isMobileDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                    if (isMobileDevice || hoveredCardRef.current === null) {
                      setShowTouchGlow(true);
                    }
                  }}
                  className={`relative transition-all duration-300 ${
                    shouldShowActive
                      ? 'w-8 h-2 bg-foreground rounded-full' 
                      : 'w-2 h-2 bg-foreground/30 rounded-full hover:bg-foreground/50'
                  } ${
                    shouldShowGlow ? 'shadow-lg' : ''
                  }`}
                  aria-label={`Go to ${services[index].title}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
